// src/modules/maintenance/hooks/useMaintenance.js
// Periyodik bakım veri yönetimi hook'u

import { useState, useMemo } from 'react';
import { db, functions } from '../../../firebase';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { calculateNextMaintenanceDate } from '../utils/maintenanceUtils';
import useCachedCollection from '../../../hooks/useCachedCollection';

export const useMaintenance = () => {
    const [actionLoading, setActionLoading] = useState({});

    // Cihaz listesi paylaşımlı oturum cache'inden gelir (Cihazlar ve SGK listesiyle
    // aynı cache). Eskiden bu sayfa açılışta 1150+ cihazı yeniden okuyup kalıcı
    // realtime listener bırakıyordu. Bakım işlemleri devices'a yazdığı için
    // her yazma sonrası refreshDevices() çağrılır.
    const { data: devicesRaw, loading, refresh: refreshDevices } = useCachedCollection('devices');

    // Bakım tarihine göre artan sırala; tarihi olmayanları sona at.
    const devices = useMemo(() => {
        const zaman = (d) => {
            if (!d) return null;
            return d.toDate ? d.toDate() : new Date(d);
        };
        return [...devicesRaw].sort((a, b) => {
            const dA = zaman(a.periyodikBakimTarihi);
            const dB = zaman(b.periyodikBakimTarihi);
            if (!dA && !dB) return 0;
            if (!dA) return 1;
            if (!dB) return -1;
            return dA - dB;
        });
    }, [devicesRaw]);

    // Müşteri geldi - Bakım yapıldı
    const handleGeldiConfirm = async (device, sonrakiBakimSuresi, sonrakiBakimBirimi) => {
        if (!device) return;

        setActionLoading(prev => ({ ...prev, [device.id]: true }));

        try {
            const nextMaintenanceDate = calculateNextMaintenanceDate(sonrakiBakimSuresi, sonrakiBakimBirimi);

            await updateDoc(doc(db, "devices", device.id), {
                periyodikBakimTarihi: Timestamp.fromDate(nextMaintenanceDate),
                sonBakimTarihi: Timestamp.fromDate(new Date()),
                bakimDurumu: null,
                bekleyenHatirlatma: false
            });
            await refreshDevices(); // cache'i tazele, liste hemen güncellensin

            const formattedDate = nextMaintenanceDate.toLocaleDateString('tr-TR');
            return { success: true, message: `Bakım kaydedildi! Sonraki bakım tarihi: ${formattedDate}` };
        } catch (error) {
            console.error("Hata:", error);
            return { success: false, message: "Bir hata oluştu: " + error.message };
        } finally {
            setActionLoading(prev => ({ ...prev, [device.id]: false }));
        }
    };

    // Müşteri gelmedi - 7 gün sonra hatırlatma
    const handleGelmedi = async (device) => {
        setActionLoading(prev => ({ ...prev, [device.id]: true }));
        try {
            const reminderDate = new Date();
            reminderDate.setDate(reminderDate.getDate() + 7);

            await updateDoc(doc(db, "devices", device.id), {
                bakimDurumu: 'Bekleniyor',
                bekleyenHatirlatma: true,
                hatirlatmaTarihi: Timestamp.fromDate(reminderDate)
            });
            await refreshDevices(); // cache'i tazele, liste hemen güncellensin
            return { success: true, message: "7 gün sonra otomatik SMS gönderilecek şekilde ayarlandı." };
        } catch (error) {
            console.error("Hata:", error);
            return { success: false, message: "Bir hata oluştu: " + error.message };
        } finally {
            setActionLoading(prev => ({ ...prev, [device.id]: false }));
        }
    };

    // SMS gönder
    const handleSendSms = async (device, message) => {
        if (!device || !message) return { success: false, message: "Geçersiz parametreler" };

        setActionLoading(prev => ({ ...prev, [device.id]: true }));
        try {
            const sendMaintenanceSms = httpsCallable(functions, 'sendMaintenanceSms');
            await sendMaintenanceSms({
                deviceId: device.id,
                message: message,
                phone: device.musteriTelefon
            });

            // Cihaz durumunu güncelle
            await updateDoc(doc(db, "devices", device.id), {
                bakimDurumu: 'Bekleniyor',
                sonSmsTarihi: Timestamp.fromDate(new Date())
            });
            await refreshDevices(); // cache'i tazele, liste hemen güncellensin

            return { success: true, message: "SMS başarıyla gönderildi!" };
        } catch (error) {
            console.error("SMS gönderim hatası:", error);
            return { success: false, message: "SMS gönderilemedi: " + error.message };
        } finally {
            setActionLoading(prev => ({ ...prev, [device.id]: false }));
        }
    };

    return {
        devices,
        loading,
        actionLoading,
        handleGeldiConfirm,
        handleGelmedi,
        handleSendSms
    };
};
